import { useState } from "react";
import type { LocalDate } from "../../domain/time";
import { recordCrewEvents } from "../../offline/crew";
import type { CrewTarget } from "../../offline/crew";
import { useOffline } from "../../offline/OfflineContext";
import { useSync } from "../../offline/SyncContext";
import { useI18n } from "../../shared/i18n/I18nContext";
import { useAnnouncer } from "../../shared/ui/Announcer";
import { formatTimeOrDateTime } from "../../shared/ui/format";
import type { ClockAction } from "./model/clockActions";
import { targetsOf } from "./model/crewActions";

/** Options of {@link useCrewRecording}. */
export interface CrewRecordingOptions {
  /** Owner key of the session. */
  readonly owner: string;
  /** Time zone of the recording person, for persons without employment zone and the times shown. */
  readonly timeZone: string;
  /** Today in that time zone. */
  readonly today: LocalDate;
}

/** State and action of {@link useCrewRecording}. */
export interface CrewRecording {
  /** Whether a step is being stored. */
  readonly busy: boolean;
  /** Whether storing the last step on the device failed. */
  readonly failed: boolean;
  /** Names of the chosen persons the last step did not fit. */
  readonly skippedNames: readonly string[];
  /**
   * Records a step for the chosen persons whose state allows it.
   *
   * @param step - The step.
   * @param chosen - The chosen persons with their local state.
   */
  readonly record: (step: ClockAction, chosen: readonly CrewTarget[]) => Promise<void>;
}

/**
 * Records crew steps on the device (T-170): one outbox event per chosen person whose state fits, the
 * names of the others, the announcement of the result and the synchronization that follows. If the
 * device cannot store an event, the step ends with a message instead of staying busy.
 *
 * @param options - Owner, time zone and today.
 * @returns The state and the action.
 */
export function useCrewRecording(options: CrewRecordingOptions): CrewRecording {
  const { t, languageTag } = useI18n();
  const { announce } = useAnnouncer();
  const offline = useOffline();
  const { syncNow } = useSync();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [skippedNames, setSkippedNames] = useState<readonly string[]>([]);

  const record = async (step: ClockAction, chosen: readonly CrewTarget[]): Promise<void> => {
    const allowed = targetsOf(step, chosen);
    setBusy(true);
    setFailed(false);

    try {
      const result = await recordCrewEvents(offline, {
        ownerSubject: options.owner,
        kind: step.kind,
        breakQualification: step.breakQualification,
        targets: allowed,
        fallbackTimeZone: options.timeZone,
        source: offline.eventSource,
      });
      const skipped = [
        ...chosen.filter((target) => !allowed.includes(target)).map((target) => target.member),
        ...result.skipped,
      ];
      setSkippedNames(skipped.map((member) => member.displayName));
      const first = result.recorded[0];

      if (first !== undefined) {
        const action =
          step.breakQualification === null
            ? t(`clock.action.${step.kind}`)
            : t(`clock.breakAction.${step.breakQualification}`);
        announce(
          t("crew.recorded", {
            action,
            count: result.recorded.length,
            time: formatTimeOrDateTime(
              first.occurredAt,
              options.today,
              languageTag,
              options.timeZone,
            ),
          }),
        );
        void syncNow();
      }
    } catch {
      // Events stored before the failure still go out with the next synchronization.
      setFailed(true);
      setSkippedNames([]);
      void syncNow();
    } finally {
      setBusy(false);
    }
  };

  const recording: CrewRecording = { busy, failed, skippedNames, record };
  return recording;
}
